/**
 * Public Acknowledgement Page — no login required
 * Staff land here after clicking the magic link in their email.
 * If the slot has a built-in template, shows the full document with
 * signature canvas + PDF download before recording the acknowledgement.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, FileText } from "lucide-react";
import PolicyTemplateViewer from "@/components/policy/PolicyTemplateViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcknowledgePage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | review | submitting | success | already | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await axios.get(`${API}/policy-ack/info/${token}`);
        setInfo(res.data);
        if (res.data.acknowledged) {
          setPhase("already");
        } else if (res.data.isTemplate && res.data.templateKey) {
          setPhase("review"); // show full document + signature UI
        } else {
          setPhase("review-upload"); // show confirmation screen for uploaded docs
        }
      } catch {
        setPhase("error");
      }
    };
    loadInfo();
  }, [token]);

  const submitAck = async () => {
    setPhase("submitting");
    try {
      const res = await axios.patch(`${API}/policy-ack/acknowledge/${token}`);
      setInfo(prev => ({ ...prev, ...res.data }));
      setPhase(res.data.alreadyDone ? "already" : "success");
    } catch {
      setPhase("error");
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : "";

  const s = { fontFamily: "Arial, sans-serif" };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4ff", ...s }}>
      {/* Header */}
      <div style={{ background: "#1a365d", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: "bold" }}>Policy Acknowledgement</div>
          {info?.policyName && <div style={{ color: "#90cdf4", fontSize: 13, marginTop: 2 }}>{info.policyName}</div>}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px 16px" }}>
        {(phase === "loading" || phase === "submitting") && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
            <Loader2 style={{ width: 48, height: 48, color: "#1a365d", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "#4a5568", fontSize: 15, marginTop: 16 }}>
              {phase === "submitting" ? "Recording your acknowledgement..." : "Loading policy..."}
            </p>
          </div>
        )}

        {phase === "review" && info?.templateKey && (
          <PolicyTemplateViewer
            templateKey={info.templateKey}
            orgName={info.orgName}
            staffName={info.staffName}
            stateCode={info.stateCode || ""}
            onAcknowledge={submitAck}
            isSubmitting={false}
          />
        )}

        {phase === "review-upload" && (
          <div style={{ maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", overflow: "hidden" }}>
            <div style={{ padding: "32px", textAlign: "center" }}>
              <FileText style={{ width: 56, height: 56, color: "#1a365d", margin: "0 auto 16px" }} />
              <h2 style={{ color: "#1a365d", fontSize: 20, fontWeight: "bold", margin: "0 0 8px" }}>Policy Acknowledgement</h2>
              <p style={{ color: "#4a5568", fontSize: 14, margin: "0 0 16px" }}>
                You have been asked to acknowledge the following policy:
              </p>
              <div style={{ background: "#f0f4ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <p style={{ color: "#1a365d", fontWeight: "bold", margin: 0, fontSize: 15 }}>{info?.policyName}</p>
              </div>
              <p style={{ color: "#4a5568", fontSize: 13, margin: "0 0 24px", lineHeight: 1.6 }}>
                The policy document was attached to the email you received. Please review it before acknowledging below.
              </p>
              <button
                onClick={submitAck}
                style={{ background: "#1a365d", color: "#fff", padding: "14px 32px", borderRadius: 8, border: "none", fontWeight: "bold", fontSize: 16, cursor: "pointer", width: "100%" }}>
                I Have Read and Acknowledge This Policy
              </button>
            </div>
          </div>
        )}

        {(phase === "success" || phase === "already") && (
          <div style={{ maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", overflow: "hidden" }}>
            <div style={{ padding: "32px", textAlign: "center" }}>
              <CheckCircle2 style={{ width: 64, height: 64, color: phase === "success" ? "#16a34a" : "#2563eb", margin: "0 auto 16px" }} />
              <h2 style={{ color: phase === "success" ? "#16a34a" : "#2563eb", fontSize: 22, fontWeight: "bold", margin: "0 0 8px" }}>
                {phase === "success" ? "Acknowledgement Recorded" : "Already Acknowledged"}
              </h2>
              <p style={{ color: "#4a5568", fontSize: 15, margin: "0 0 16px" }}>
                {phase === "success"
                  ? <>Thank you, <strong>{info?.staffName}</strong>. You have acknowledged:</>
                  : <><strong>{info?.staffName}</strong>, you have already acknowledged this policy.</>}
              </p>
              <div style={{ background: phase === "success" ? "#f0fdf4" : "#eff6ff", border: `1px solid ${phase === "success" ? "#86efac" : "#93c5fd"}`, borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
                <p style={{ color: phase === "success" ? "#166534" : "#1e40af", fontWeight: "bold", margin: "0 0 6px", fontSize: 15 }}>{info?.policyName}</p>
                <p style={{ color: "#4a5568", fontSize: 13, margin: 0 }}>Acknowledged on: {formatDate(info?.acknowledgedAt)}</p>
              </div>
              <p style={{ color: "#718096", fontSize: 13 }}>
                Remember to upload your signed copy to your Staff Records. You may now close this page.
              </p>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div style={{ maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 12, padding: 32, textAlign: "center" }}>
            <AlertCircle style={{ width: 64, height: 64, color: "#dc2626", margin: "0 auto 16px" }} />
            <h2 style={{ color: "#dc2626", fontSize: 22, fontWeight: "bold" }}>Invalid Link</h2>
            <p style={{ color: "#4a5568", fontSize: 15 }}>This link is invalid or has expired. Please contact your director for a new link.</p>
          </div>
        )}
      </div>
    </div>
  );
}
